/**
 * Client-side SEO utilities for meta generation
 *
 * These functions are used by client components to generate SEO meta tags.
 * They take a translation function (t) as the first argument.
 */

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import type {
  Artwork,
  GenerationModel,
  Manga,
  User,
} from 'src/services/apollo/generated/graphql'

const SITE_NAME = 'PixAI'

type Maybe<T> = T | null | undefined

// ============================================================================
// Helper functions
// ============================================================================

function getUserDisplayName(
  user: Maybe<{ displayName?: string | null; username?: string | null }>,
) {
  return user?.displayName || user?.username || 'Anonymous'
}

// ============================================================================
// Artwork SEO
// ============================================================================

export function getArtworkMetaTitle(
  t: TFunction,
  artwork: Maybe<
    Pick<Artwork, 'title'> & {
      author?: Maybe<Pick<User, 'displayName' | 'username'>>
    }
  >,
): string {
  if (!artwork) return t('common:meta.title.home')

  const title = artwork.title || 'AI Artwork'
  const authorName = getUserDisplayName(artwork.author)
  const mainPart = t('common:meta.title.artwork', { title, authorName })
  return `${mainPart} | ${SITE_NAME}`
}

export function getArtworkMetaDescription(
  t: TFunction,
  artwork: Maybe<
    Pick<Artwork, 'title' | 'prompts' | 'hidePrompts'> & {
      author?: Maybe<Pick<User, 'displayName' | 'username'>>
      tacks?: Maybe<
        Array<{
          displayName?: string | null
          defaultName: string
        }>
      >
    }
  >,
): string {
  if (!artwork) return t('common:meta.description.home')

  const title = artwork.title || 'AI Artwork'
  const authorName = getUserDisplayName(artwork.author)
  const tags =
    artwork.tacks
      ?.map(tack => tack.displayName ?? tack.defaultName)
      .join(', ') || ''
  const prompts = artwork.hidePrompts ? '' : artwork.prompts || ''

  return t('common:meta.description.artwork', {
    title,
    authorName,
    prompts,
    tags,
  })
}

// ============================================================================
// Artwork Relevant SEO
// ============================================================================

export function getArtworkRelevantMetaTitle(
  t: TFunction,
  artwork: Maybe<Pick<Artwork, 'title'>>,
): string {
  if (!artwork) return t('common:meta.title.home')

  const title = artwork.title || 'AI Artwork'
  return t('artwork:relevant-page.meta.title', {
    title,
    defaultValue: `Related to ${title} | ${SITE_NAME}`,
  })
}

export function getArtworkRelevantMetaDescription(
  t: TFunction,
  artwork: Maybe<Pick<Artwork, 'title'>>,
): string {
  if (!artwork) return t('common:meta.description.home')

  const title = artwork.title || 'AI Artwork'
  return t('artwork:relevant-page.meta.description', {
    title,
    defaultValue: `Discover artworks related to ${title}`,
  })
}

// ============================================================================
// Model SEO
// ============================================================================

export function getModelMetaTitle(
  t: TFunction,
  model: Maybe<
    Pick<GenerationModel, 'title' | 'type' | 'extra'> & {
      tags?: Maybe<Array<{ name: string }>>
    }
  >,
): string {
  if (!model) return t('common:meta.title.home')

  const extra = model.extra as { description?: string } | null
  const vars = {
    title: model.title || 'Model',
    description: extra?.description ?? '',
    tags: model.tags?.map(tag => tag.name).join(', ') || '',
  }

  const isLora = model.type === 'LORA'
  const mainPart = isLora
    ? t('common:meta.title.lora', vars)
    : t('common:meta.title.sdv1model', vars)

  return `${mainPart} | ${SITE_NAME}`
}

export function getModelMetaDescription(
  t: TFunction,
  model: Maybe<
    Pick<GenerationModel, 'title' | 'type' | 'extra'> & {
      tags?: Maybe<Array<{ name: string }>>
    }
  >,
): string {
  if (!model) return t('common:meta.description.home')

  const extra = model.extra as { description?: string } | null
  const vars = {
    title: model.title || 'Model',
    description: extra?.description ?? '',
    tags: model.tags?.map(tag => tag.name).join(', ') || '',
  }

  const isLora = model.type === 'LORA'
  return isLora
    ? t('common:meta.description.lora', vars)
    : t('common:meta.description.sdv1model', vars)
}

export function getModelCoverAlt(
  t: TFunction,
  model: Maybe<Pick<GenerationModel, 'title' | 'type'>>,
): string {
  if (!model)
    return t('common:model.cover-alt.default', {
      defaultValue: 'Model cover image',
    })

  const title = model.title || 'Model'
  const isLora = model.type === 'LORA'
  const typeLabel = isLora ? 'LoRA' : 'AI Model'

  return t('common:model.cover-alt', {
    title,
    type: typeLabel,
    defaultValue: `${title} - ${typeLabel} cover image`,
  })
}

// ============================================================================
// Manga SEO
// ============================================================================

export function getMangaMetaTitle(
  t: TFunction,
  manga: Maybe<
    Pick<Manga, 'title'> & {
      author?: Maybe<Pick<User, 'displayName' | 'username'>>
    }
  >,
): string {
  if (!manga) return t('common:meta.title.home')

  const title = manga.title || 'AI Manga'
  const authorName = getUserDisplayName(manga.author)
  const mainPart = t('common:meta.title.artwork', { title, authorName })
  return `${mainPart} | ${SITE_NAME}`
}

export function getMangaMetaDescription(
  t: TFunction,
  manga: Maybe<Pick<Manga, 'title'>>,
): string {
  if (!manga) return t('common:meta.description.home')

  const title = manga.title || 'AI Manga'
  return t('common:meta.description.manga', { title })
}

// ============================================================================
// User Profile SEO
// ============================================================================

export function getUserTitle(
  t: TFunction,
  user: Maybe<Pick<User, 'displayName' | 'username'>>,
): string {
  if (!user) return t('common:meta.title.home')

  const displayName = getUserDisplayName(user)
  return t('common:meta.title.user.home', {
    username: displayName,
    defaultValue: `${displayName}'s Profile | ${SITE_NAME}`,
  })
}

export function getUserMetaDescription(
  t: TFunction,
  user: Maybe<Pick<User, 'displayName' | 'username'>>,
  currentTab?: string,
): string {
  if (!user) return t('common:meta.description.home')

  const displayName = getUserDisplayName(user)
  const tabContext = currentTab ? ` - ${currentTab}` : ''

  return t('common:meta.description.user', {
    username: displayName,
    tab: tabContext,
    defaultValue: `View ${displayName}'s AI artworks, models, and creations on ${SITE_NAME}.${tabContext}`,
  })
}

// ============================================================================
// Tag Page SEO
// ============================================================================

export function getTagPageMetaTitle(
  t: TFunction,
  type: 'artwork' | 'model',
  tagName: string,
  query?: string,
): string {
  const typeLabel = type === 'artwork' ? 'AI Artworks' : 'AI Models'

  if (query) {
    return t('common:meta.title.tag-search', {
      tag: tagName,
      type: typeLabel,
      query,
      defaultValue: `#${tagName} ${typeLabel} - "${query}" | ${SITE_NAME}`,
    })
  }

  return t('common:meta.title.tag', {
    tag: tagName,
    type: typeLabel,
    defaultValue: `#${tagName} ${typeLabel} | ${SITE_NAME}`,
  })
}

export function getTagPageMetaDescription(
  t: TFunction,
  type: 'artwork' | 'model',
  tagName: string,
  query?: string,
  totalCount?: number,
): string {
  const typeLabel = type === 'artwork' ? 'AI artworks' : 'AI models'

  if (query && totalCount !== undefined) {
    return t('common:meta.description.tag-search', {
      tag: tagName,
      type: typeLabel,
      query,
      count: totalCount,
      defaultValue: `Found ${totalCount} ${typeLabel} tagged with #${tagName} matching "${query}" on ${SITE_NAME}.`,
    })
  }

  if (totalCount !== undefined) {
    return t('common:meta.description.tag-with-count', {
      tag: tagName,
      type: typeLabel,
      count: totalCount,
      defaultValue: `Browse ${totalCount} ${typeLabel} tagged with #${tagName} on ${SITE_NAME}.`,
    })
  }

  return t('common:meta.description.tag', {
    tag: tagName,
    type: typeLabel,
    defaultValue: `Browse ${typeLabel} tagged with #${tagName} on ${SITE_NAME}.`,
  })
}

// ============================================================================
// Artwork List Tag Query SEO (Hooks)
// ============================================================================

interface TagInfo {
  displayName?: string | null
  name?: string | null
}

interface UseArtworkListTagQueryOptions {
  q?: string
  tag: string
  tagInfo: TagInfo
}

export function useArtworkListTagQueryTitle(
  options: UseArtworkListTagQueryOptions,
): string {
  const { t } = useTranslation('common')
  const { q, tag, tagInfo } = options

  if (q) return t('common:meta.title.tag-artwork-list', { tag: q })
  if (tag) {
    return t('common:meta.title.tag-artwork-list', { tag: `#${tagInfo.name}` })
  }

  return ''
}

export function useArtworkListTagQueryDescription(
  options: UseArtworkListTagQueryOptions,
): string {
  const { t } = useTranslation('common')
  const { tag, tagInfo } = options

  if (!tag) {
    return t('meta.description.home')
  }

  const displayName = tagInfo?.displayName || tagInfo?.name || tag
  return getTagPageMetaDescription(t, 'artwork', displayName)
}
